import { buildSystemPrompt, buildFirstMessage } from '@/lib/systemPromptBuilder';
import { buildAgentPrompt } from './index';
import type { AdapterContext } from './adapters';

/**
 * The single version gate between the legacy builders and the V2 compiler.
 *
 * Strict by design: V2 runs ONLY when an agent has been explicitly converted
 * (`prompt_schema_version === 2`). Anything else — 1, null, undefined, a string,
 * a half-converted agent — keeps its existing builder and its existing
 * behaviour, unchanged. There is no inference from field completeness: an agent
 * that merely happens to have five sections filled must not silently change how
 * it talks to a Boston learner mid-training.
 *
 * Flipping `prompt_schema_version` back to 1 is the per-agent rollback, and it
 * needs no code change or deploy.
 */
export function usesV2Prompt(agent: Record<string, any> | null | undefined): boolean {
  // Strictly the number 2 — no coercion. `Number("2") === 2` would let a
  // stringified or malformed value switch a live Boston agent onto a different
  // prompt mid-training. The column is a Postgres integer, so anything that
  // arrives as a string is a bug somewhere upstream, and the safe response is
  // to keep the agent on its existing builder rather than guess.
  return agent?.prompt_schema_version === 2;
}

/**
 * Build the live system prompt for a non-coach agent
 * (patient / customer / examiner, on Anam or ElevenLabs).
 *
 * Coaches are gated inside their own session pages: their V1 path is
 * `buildCoachingPrompt`, which lives in the page and needs the output template
 * and learner memory that only the page has.
 */
export function buildRuntimePrompt(agent: Record<string, any>, ctx: AdapterContext = {}): string {
  if (!usesV2Prompt(agent)) return buildSystemPrompt(agent as any);
  return buildAgentPrompt(agent, ctx).prompt;
}

/**
 * Build the avatar's opening line, version-gated.
 *
 * Why this exists rather than a fix inside `buildFirstMessage`: that function's
 * fallbacks introduce the agent by `agent.name`, which is the tile/session title
 * ("Sale call planning"), not the persona ("Sam"). A V2 agent would open with
 * "Hello, Sale call planning here." — the same two-identity bug as the system
 * prompt, but spoken out loud as the very first thing the learner hears.
 *
 * It cannot be fixed unconditionally in the V1 builder: `persona_name` is
 * persisted for V1 agents too (so that turning V2 off stays a reversible
 * rollback rather than losing the author's work), so keying off its mere
 * presence would silently change a live V1 agent's opening.
 *
 * So: V1 agents go through the legacy function untouched — byte-for-byte. V2
 * agents reuse the same fallback logic with the persona substituted for the
 * title, which keeps one implementation of the wording.
 */
export function buildRuntimeFirstMessage(agent: Record<string, any>): string {
  if (!usesV2Prompt(agent)) return buildFirstMessage(agent as any);

  // A configured opening line always wins, in either version.
  if (agent?.first_message) return buildFirstMessage(agent as any);

  const persona = String(agent?.persona_name ?? '').trim();
  if (!persona) return buildFirstMessage(agent as any);

  return buildFirstMessage({ ...agent, name: persona } as any);
}
